import React from "react";
import { Container, TextField, Button, Typography, Grid, Checkbox, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Box, CircularProgress, useMediaQuery, FormLabel, Link } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import carchaserWhiteLogo from "../../../components/assets/carchaser-white-logo.svg";
import theme from "../../../theme";
import api from "../../../api";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setAlertDialog } from "../../../toolkit/alertDialog/alertDialogSlice";
import { useNavigate } from "react-router-dom";
import HeroImg from "../../../components/assets/hero-img.png";
import { routes } from "../../../utils/constants/routes";
import { useLazyGetCitiesQuery, useLazyGetCountriesQuery, useLazyGetStatesQuery } from "../../../toolkit/geography/geographyApi";
import { USER_ROLES, USER_ROLES_DOMAINS } from "../../../utils/constants/users/userRoles";
import { useSignupMutation } from "src/toolkit/auth/signupApi";
import { preferredCarBrands } from "@common/utils/constants/carData";
import FieldErrorText from "@common/components/ui/FieldErrorText";
import TermsAndConditionsModal from "@common/components/custom/TermsModal";

const textFieldStyles = {
  background: theme.palette.background.paper,
  '& input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
    WebkitTextFillColor: '#000',
  },
};

const FormInputSelect = ({ name, showLabel, label, formik, options,
  sx
}) => {
  return (
    <Grid item sx={{
      width: "47%",
      ...(sx || {})
    }}>
      {showLabel ? <FormLabel
        fontWeight={500}
        fontSize={14}
        mb={0.5}
        sx={{
          color: theme.palette.text.primary,
        }}
      >
        {label}
      </FormLabel> : null}
      <Select
        name={name}
        value={formik?.values?.[name]}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        displayEmpty
        sx={{
          width: "100%",
          // height: 40,
          "& .MuiSelect-select": {
            opacity: !formik?.values?.[name] ? "0.5" : "1"
          },
        }}
      >
        <MenuItem
          value={""}
          disabled
          sx={{
            display: "none",
          }}
        >
          {label}
        </MenuItem>
        {options?.map?.(option => {
          return (
            <MenuItem key={option?.value} value={option?.value}>
              {option?.label}
            </MenuItem>
          );
        })}
      </Select>

      {formik?.touched?.[name] && formik?.errors?.[name] ? (
        <FieldErrorText>
          {formik?.errors?.[name]}
        </FieldErrorText>
      ) : null}
    </Grid>
  )
}

const DealerSignup = () => {

  const isMobile = useMediaQuery("(max-width:1050px)");

  // navigation hooks start...
  // useNavigate hook...
  const navigate = useNavigate();
  // useNavigate hook...
  // navigation hooks end...

  // useDispatch...
  const dispatch = useDispatch();
    const { t } = useTranslation("signUp");
  // useDispatch...

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    dealer_name: "",
    dealershipId: "",
    preferredBrand: "",
    salesperson_registration: "",
    dealer_full_address: "",
    dealer_address: "",
    agreeToTerms: false,
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().required("This field is required"),
    last_name: Yup.string().required("This field is required"),
    email: Yup.string().email("Invalid email").required("This field is required"),
    phone: Yup.string().required("This field is required"),
    country: Yup.string().required("This field is required."),
    state: Yup.string().required("This field is required."),
    city: Yup.string().required("This field is required."),
    dealer_name: Yup.string().required("This field is required"),
    dealershipId: Yup.string().required("This field is required"),
    preferredBrand: Yup.string().required("This field is required"),
    salesperson_registration: Yup.string().required("This field is required"),
    dealer_full_address: Yup.string().required("This field is required"),
    dealer_address: Yup.string().required("This field is required"),
    agreeToTerms: Yup.boolean().oneOf([true], "You must agree to the terms")
  });

  // useState variables start...
  const [isSignupLoading, setIsSignupLoading] = React.useState(false);
  const [countries, setCountries] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [signupUserRole, setSignupUserRole] = React.useState("");
  const [openTermsModal, setOpenTermsModal] = React.useState(false);
  // useState variables end...

  // custom hooks start...
  const [getCountriesApiHandler, getCountriesApiData] = useLazyGetCountriesQuery();
  const [getStatesApiHandler, getStatesApiData] = useLazyGetStatesQuery();
  const [getCitiesApiHandler, getCitiesApiData] = useLazyGetCitiesQuery();
  const [signupApiHandler, signupApiData] = useSignupMutation();
  // custom hooks end...

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {

      setIsSignupLoading(true);

      try {
        const payload = {
          ...values
        };

        const response = await signupApiHandler({
          payload
        });

        console.log(response, `signupApiHandler`)

        if (response?.error || ("status" in response?.data && !response?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error!`,
              message: response?.error?.data?.message || response?.error?.data?.error || response?.error?.message || response?.data?.message || `Some error occured!`
            })
          )
        } else {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Success!`,
              message: `Signup was successful!`,
              onOk: () => {
                navigate(
                  routes.authSignin
                );
              }
            })
          );
        }
      } catch (err) {
        console.log(err, "signupError");
        dispatch(
          setAlertDialog({
            open: true,
            title: "Error!",
            message: err?.response?.data?.error
          })
        )
      } finally {
        setIsSignupLoading(false);
      }
    },
  });

  React.useEffect(() => {
    (async () => {
      const res = await getCountriesApiHandler();

      if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.error || res?.error?.data?.message ||
              res?.data?.message || `Some error occured in fetching countries!`
          })
        );
        return;
      }

      console.log(res, `getCountriesApiHandler`)

      setCountries(res?.data?.map?.(country => {
        return {
          value: String(country?.id),
          label: country?.name
        }
      }) || []);
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (formik?.values?.country) {
        const res = await getStatesApiHandler({
          countryId: formik?.values?.country
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error!`,
              message: res?.error?.data?.error || res?.error?.data?.message ||
                res?.data?.message || `Some error occured in fetching states!`
            })
          );
          return;
        }

        setStates(res?.data?.map?.(state => {
          return {
            value: String(state?.id),
            label: state?.name
          }
        }) || []);
      }
    })()
    
  }, [formik?.values?.country]);

  React.useEffect(() => {
    (async () => {
      if (formik?.values?.state) {
        const res = await getCitiesApiHandler({
          stateId: formik?.values?.state
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error!`,
              message: res?.error?.data?.error || res?.error?.data?.message ||
                res?.data?.message || `Some error occured in fetching cities!`
            })
          );
          return;
        }

        setCities(res?.data?.map?.(city => {
          return {
            value: String(city?.id),
            label: city?.name
          }
        }) || []);
      }
    })()
    
  }, [formik?.values?.state]);

  // React.useEffect(() => {
  //   if (signupUserRole) {
  //     dispatch(
  //       setAlertDialog({
  //         open: true,
  //         title: `Alert!`,
  //         message: `Are you sure you want to sign up as ${signupUserRole}?`,
  //         onOk: () => {
  //           window.location = USER_ROLES_DOMAINS[signupUserRole]
  //         }
  //       })
  //     )
  //   }
  // }, [signupUserRole]);
  // useEffect end

  return (
    <Grid container 
      sx={{ 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "stretch",
        width: "100%",
        margin: "0 auto",
      }}
    >
      {!isMobile ? (
        <Grid item sx={{ 
          width: "52%",
          borderRadius: "1.5rem",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          // height: "90vh",
          backgroundImage: `url(${HeroImg})`, 
          backgroundSize: "cover", 
          backgroundPosition: "center", 
          p: 4, color: "white" 
        }}>
          {/* <Typography variant="h4" fontWeight="bold">CAR CHASER</Typography> */}
          <img 
            src={carchaserWhiteLogo}
            style={{
              width: "18rem",
              // height: "100%"
            }}
          />
          
          <Box
            sx={{
              padding: "1rem",
              background: "rgba(0, 0, 0, 0.8)",
              borderRadius: "1rem"
            }}
          >
            <Typography variant="h6">Contact Information</Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >20-4534 Dufferin St,</Typography>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >North York M3H 5X2</Typography>
              </Box>
              <Box>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >Mon - Fri: 9am - 7pm</Typography>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >Sat: 10am - 5pm | Sun: Closed</Typography>
              </Box>
              <Box>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >Phone: 647-893-6208</Typography>
                <Typography variant="body2"
                  sx={{
                    color: "white"
                  }}
                >Email: contact@carchaser.ca</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      ) : null} 
      
      <Grid item sx={{ 
        width: isMobile ? "100%" : "45%",
        padding: "0",
        display: "flex", justifyContent: "flex-end", alignItems: "center", bgcolor: "#f8f9fa",
      }}>
        <Box
          sx={{ 
            maxWidth: isMobile ? "none" : "45rem",
            padding: "1.5rem 2rem !important",
            boxShadow: 3, 
            borderRadius: "1.5rem",
            bgcolor: "white" 
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>{t("dealerSignup.dealerSignup")}</Typography>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            {t("dealerSignup.welcome")}{" "}
            <Typography
              component={"span"}
              sx={{
                fontWeight: "700",
                color: theme.palette.primary.main
              }}
            >
              CAR CHASER
            </Typography>
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}
              sx={{
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.firstName")} name="first_name" {...formik.getFieldProps("first_name")} error={formik.touched.first_name && Boolean(formik.errors.first_name)} helperText={formik.touched.first_name && formik.errors.first_name} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.lastName")} name="last_name" {...formik.getFieldProps("last_name")} error={formik.touched.last_name && Boolean(formik.errors.last_name)} helperText={formik.touched.last_name && formik.errors.last_name} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.email")} name="email" {...formik.getFieldProps("email")} error={formik.touched.email && Boolean(formik.errors.email)} helperText={formik.touched.email && formik.errors.email} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.phone")} name="phone" {...formik.getFieldProps("phone")} error={formik.touched.phone && Boolean(formik.errors.phone)} helperText={formik.touched.phone && formik.errors.phone} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.dealershipName")} name="dealer_name" {...formik.getFieldProps("dealer_name")} error={formik.touched.dealer_name && Boolean(formik.errors.dealer_name)} helperText={formik.touched.dealer_name && formik.errors.dealer_name} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.dealershipId")} name="dealershipId" {...formik.getFieldProps("dealershipId")} error={formik.touched.dealershipId && Boolean(formik.errors.dealershipId)} helperText={formik.touched.dealershipId && formik.errors.dealershipId} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}>
                <FormInputSelect name="preferredBrand" showLabel={false} label={t("dealerSignup.form.Preferred car brands")} formik={formik} 
                  options={preferredCarBrands?.map?.(brand => {
                    return {
                      value: brand,
                      label: t(`dealerSignup.form.brands.${brand}`)
                    }
                  })}
                  sx={{
                    width: "100%"
                  }}
                />
                {/* <FormControl fullWidth error={formik.touched.preferredBrand && Boolean(formik.errors.preferredBrand)}>
                  <InputLabel id="preferred-brand-label">{t("dealerSignup.form.preferredBrand")}</InputLabel>
                  <Select labelId="preferred-brand-label" id="preferredBrand" name="preferredBrand" value={formik.values.preferredBrand} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                    <MenuItem value="Toyota">{t("dealerSignup.form.brands.toyota")}</MenuItem>
                    <MenuItem value="Honda">{t("dealerSignup.form.brands.honda")}</MenuItem>
                    <MenuItem value="Ford">{t("dealerSignup.form.brands.Ford")}</MenuItem>
                  </Select>
                </FormControl> */}
              </Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.salespersonRegistration")} name="salesperson_registration" {...formik.getFieldProps("salesperson_registration")} error={formik.touched.salesperson_registration && Boolean(formik.errors.salesperson_registration)} helperText={formik.touched.salesperson_registration && formik.errors.salesperson_registration} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.address")} name="dealer_full_address" {...formik.getFieldProps("dealer_full_address")} error={formik.touched.dealer_full_address && Boolean(formik.errors.dealer_full_address)} helperText={formik.touched.dealer_full_address && formik.errors.dealer_full_address} sx={{...textFieldStyles}} /></Grid>
              <Grid item sx={{ width: "47%" }}><TextField fullWidth label={t("dealerSignup.form.postalCode")} name="dealer_address" {...formik.getFieldProps("dealer_address")} error={formik.touched.dealer_address && Boolean(formik.errors.dealer_address)} helperText={formik.touched.dealer_address && formik.errors.dealer_address} sx={{...textFieldStyles}} /></Grid>
              <FormInputSelect name="country" showLabel={false} label={t("dealerSignup.form.selectCountry")} formik={formik} 
                options={countries}
              />
              <FormInputSelect name="state" showLabel={false} label={t("dealerSignup.form.selectState")} formik={formik} 
                options={states}
              />
              <FormInputSelect name="city" showLabel={false} label={t("dealerSignup.form.selectCity")} formik={formik} 
                options={cities}
              />
            </Grid>
            
            <Box mt={2}>
              <Typography variant="body2" gutterBottom
                sx={{
                  color: theme.palette.text.primary,
                  padding: "1.5rem 0",
                  lineHeight: "1.35rem"
                }}
              >
                {t("dealerSignup.privacyNotice")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center"
                }}
              >
                <FormControlLabel 
                  control={
                    <Checkbox 
                      name="agreeToTerms" 
                      checked={formik.values.agreeToTerms} 
                      onChange={formik.handleChange} 
                      sx={{
                        '& .MuiSvgIcon-root': {
                          border: `1px solid ${theme.palette.text.primary}`,
                          borderRadius: '4px', // square edges
                        }
                      }}
                    />
                  } 
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setOpenTermsModal(true);
                      }}
                    >
                      {t("dealerSignup.termsAgreement1")}{" "}
                      <Link href="#">{t("dealerSignup.termsAgreement2")}</Link>.
                    </Typography>
                  } 
                  sx={{
                    width: "60%",
                  }}
                />
                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && <Typography color="error" variant="caption">{formik.errors.agreeToTerms}</Typography>}
              </Box>
            </Box>
            <Button type="submit" variant="contained" fullWidth 
              sx={{ 
                mt: 2, bgcolor: "#4c34f0", color: "white", borderRadius: "24px", 
                fontSize: "1rem", padding: "12px" 
              }}
              disabled={isSignupLoading || (
                (Object.entries(formik.touched)?.length === 0 || Object.entries(formik.errors).length > 0)
              )}
            >
              {isSignupLoading ? (
                <CircularProgress 
                  size={22}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                />
              ) : (
                t("dealerSignup.signUpButton")
              )}
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.25rem"
              }}
            >
              <Typography
                textAlign="center"
                marginTop={2}
                sx={{
                  color: theme.palette.text.primary,
                }}
              >
                <Button
                  variant="text"
                  sx={{
                    padding: "0px",
                    minWidth: "0px",
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    // textDecoration: "underline",
                    "&:hover": {
                      backgroundColor: "transparent",
                      // textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    // Handle sign-in navigation, e.g. navigate("/login")
                    // navigate();
                    window.location = USER_ROLES_DOMAINS.seller;
                  }}
                >
                  Sign up as a Seller
                </Button>
              </Typography>
            </Box>
              
            <Typography textAlign="center" marginTop={2}
              sx={{
                color: theme.palette.text.primary
              }}
            >
              {t("dealerSignup.alreadyHaveAccount")}{" "}
              <Button
                variant="text"
                sx={{
                  padding: "0px",
                  minWidth: "0px",
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                  textTransform: "none",
                  textDecoration: "underline",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
                onClick={() => {
                  // Handle sign-in navigation, e.g. navigate("/login")
                  navigate(routes.authSignin);
                }}
              >
                {t("dealerSignup.signInLink")}
              </Button>
            </Typography>
          </form>
        </Box>
      </Grid>

      <TermsAndConditionsModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        onAccept={() => {
          setOpenTermsModal(false);
          formik.setFieldValue("agreeToTerms", true);
        }}
      />
    </Grid>
  );
};

export default DealerSignup;
